import { get, filter, forEach, isEmpty, has, isArray, map, isUndefined } from 'lodash';

// local dependencies
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';

// get a global redux store ( creates empty store if not already created )
const store = getStore();

/**
 * @type {function}
 * @desc Resolvers visible items.
 * @param {object} items - an array of items with `visibility` key
 * @param {object} visibility - an object containing `item.name:bool` pairs
 * @example
 * import { getVisibleItems } from 'resolvers/getVisibleEntities';
 */
export const getVisibleItems = ( items, visibility ) => {
    return filter( items, item => {
        return false !== get( visibility, item.visibility ); // return `false` only-if `visibility[ref]` is `false`
    } );
};

/*--------------*/

/**
 * @type {function}
 * @desc Return true if the object is visible.
 * @param {object} item - an object with `visibility` key
 * @param {object} visibility - an object containing `item.name:bool` pairs
 * @example
 * import { isVisible } from 'resolvers/getVisibleEntities';
 */
export const isVisible = ( item, visibility ) => {
    return false !== get( visibility, item.visibility ); // return `false` only-if `visibility[ref]` is `false`
};

/*--------------*/

/**
 * @type {function}
 * @desc Resolvers visible widgets.
 * @param {object} component - `component` part of the response.
 * @example
 * import { getVisibleWidgets } from 'resolvers/getVisibleEntities';
 */
export const getVisibleWidgets = ( component = {} ) => {

    // an object representing visibility of the widgets
    const visibility = get( component, 'visibility', {} );

    // widgets list
    const widgets = get( component, 'structure.widgets', [] );

    // return widgets that has
    return filter( widgets, widget => {
        return false !== get( visibility, widget.visibility ); // return `false` only-if `visibility[ref]` is `false`
    } );
};

/*--------------*/

/**
 * @type {function}
 * @desc Resolvers visible columns of a table.
 * @param {object} component - `component` part of the response.
 * @example
 * import { getVisibleTableEntities } from 'resolvers/getVisibleEntities';
 */
export const getVisibleTableEntities = ( component = {} ) => {
    const visibility = get( component, 'visibility', {} );
    const headColumns = get( component, 'structure.headColumns', [] );
    const primaryColumns = get( component, 'structure.primaryColumns', [] );
    const secondaryColumns = get( component, 'structure.secondaryColumns', [] );
    const filters = get( component, 'structure.filtersConfig.filters', [] );

    // get options from app context
    const contextOptions = get( store.getState()[ REDUCER.APP_CONTEXT ], 'component.data.options', {} );

    /*-------*/

    // filter primary and secondary columns
    const _primaryColumns = filter( primaryColumns, column => {
        return false !== get( visibility, column.visibility ); // return `false` only-if `visibility[ref]` is `false`
    } );

    const _secondaryColumns = filter( secondaryColumns, column => {
        return false !== get( visibility, column.visibility ); // return `false` only-if `visibility[ref]` is `false`
    } );

    const _headColumns = filter( headColumns, column => {
        return false !== get( visibility, column.visibility ); // return `false` only-if `visibility[ref]` is `false`
    } );

    // add options to columns from `data`
    forEach( [ ..._primaryColumns, ..._secondaryColumns, ..._headColumns ], ( column = {} ) => {
        if( ! isEmpty( column.options ) ) {
            if( isArray( column.options ) ) {
                column.options = map( column.options, ( optionsSetName ) => {
                    if( has( component, `data.options.${ optionsSetName }` ) ) {
                        return get( component, `data.options.${ optionsSetName }`, [] );
                    } else if( has( contextOptions, optionsSetName ) ) {
                        return get( contextOptions, optionsSetName, [] );
                    } else {
                        return [];
                    }
                } );
            } else {
                if( has( component, `data.options.${ column.options }` ) ) {
                    column.options = get( component, `data.options.${ column.options }`, [] );
                } else if( has( contextOptions, column.options ) ) {
                    column.options = get( contextOptions, column.options, [] );
                } else {
                    column.options = [];
                }
            }
        }

        if( ! isEmpty( get( column, 'config', {} ) ) ) {
            if( isArray( column.config.options ) ) {
                column.config.options = map( column.config.options, ( optionsSetName ) => {
                    if( has( component, `data.options.${ optionsSetName }` ) ) {
                        return get( component, `data.options.${ optionsSetName }`, [] );
                    } else if( has( contextOptions, optionsSetName ) ) {
                        return get( contextOptions, optionsSetName, [] );
                    } else {
                        return [];
                    }
                } );
            } else {
                if( has( component, `data.options.${ column.config.options }` ) ) {
                    column.config.options = get( component, `data.options.${ column.config.options }`, [] );
                } else if( has( contextOptions, column.config.options ) ) {
                    column.options = get( contextOptions, column.config.options, [] );
                } else {
                    column.config.options = [];
                }
            }
        }

    } );

    /*-------*/

    // format global filters
    const _filters = filter( filters, item => {
        return false !== get( visibility, item.visibility ); // return `false` only-if `visibility[ref]` is `false`
    } );

    // add initial filter value from `data.state`
    forEach( _filters, ( item = {} ) => {
        item.value = get( component, `data.state.${ item.name }`, null );

        if( ! isEmpty( get( item, 'config.options' ) ) ) {
            const optionsSetName =  get( item, 'config.options' );
            
            item.config.options = get( component, `data.options.${ optionsSetName }`, [] );
        }

        const advancedOptions = get( item, 'config.advancedOptions', {} );

        if( !isEmpty( advancedOptions ) && !isArray( advancedOptions.options ) ) {

            if( has( component, `data.options.${ advancedOptions.options }` ) ) {
                advancedOptions.options = get( component, `data.options.${ advancedOptions.options }`, [] );
            } else {
                advancedOptions.options = [];
            }
        }

    } );

    // get the reference of option
    const tabOptionKey = get( component, 'structure.tabs.options', undefined );
    const payloadKey = get( component, 'structure.tabs.payloadKey', undefined );

    // get the tabs list
    const tabs = ! isUndefined( tabOptionKey ) ? get( component, `data.options.${ tabOptionKey }`, [] ): [];

    const selectedTab = ! isUndefined( payloadKey ) ? { [ payloadKey ]: get( component, `data.state.${ payloadKey }`, undefined ) }: {};

    /*-------*/

    return {
        columns: {
            primary: _primaryColumns,
            secondary: _secondaryColumns,
            head: _headColumns
        },
        tabsConfig: {
            options: tabs,
            payloadKey: ! isUndefined( tabOptionKey ) ? get( component, 'structure.tabs.payloadKey', undefined ) : undefined,
        },
        selectedTab,
        filters: _filters
    };

};

